import { ApolloError, LazyQueryResult, QueryResult } from '@apollo/client';

import { isProduction } from 'utils';

import { ErrorFallback } from '../ErrorBoundary/components/ErrorFallback';
import Loader from '../Util/Loader';

interface Props<T> {
  query: QueryResult<T, any> | QueryResult<any, any>[];
  lazyQuery?: LazyQueryResult<any, any> | LazyQueryResult<any, any>[];
  queryName?: string;
  onError?: (error: ApolloError) => any;
  ignoreErrors?: boolean;
  children: (data) => JSX.Element | null | undefined;
  hideLoader?: boolean;
}

export default function GQLGuard<T>({
  query,
  lazyQuery,
  ignoreErrors,
  onError,
  children,
  hideLoader
}: Props<T>) {
  if (Array.isArray(lazyQuery)) {
    const lazyQueries = lazyQuery as LazyQueryResult<any, any>[];
    if (lazyQueries.some(lq => lq.loading)) {
      return <Loader backdrop={false} />;
    }

    if (lazyQueries.some(lq => !!lq.error)) {
      lazyQueries
        .filter(q => !!q.error)
        .forEach(({ error }) => {
          if (!isProduction()) {
            error!.graphQLErrors.forEach(e => {
              console.error(`[ERROR]{${e.path?.join(' > ')}}: `, e);
            });
          }
        });

      if (onError) {
        return onError(lazyQueries[0].error!);
      }

      if (!isProduction()) {
        return lazyQueries
          .filter(q => !!q.error)
          .map(({ error }) =>
            error!.graphQLErrors.map(e => {
              if ((e.extensions?.exception as any)?.code === 'ENOTFOUND') {
                return (
                  <ErrorFallback
                    error={new Error(`Service "${e.message.split(' ')[2]}" is not reachable`)}
                    componentStack={null}
                  />
                );
              }
              return (
                <ErrorFallback
                  error={new Error(e.message)}
                  componentStack={JSON.stringify(e.extensions?.exception, undefined, 2)}
                />
              );
            })
          );
      }

      return null;
    }
  } else {
    if (lazyQuery?.loading) {
      return <Loader backdrop={false} />;
    }

    if (lazyQuery?.error) {
      if (!isProduction()) {
        lazyQuery?.error.graphQLErrors.forEach(e => {
          console.error(`[ERROR]{${e.path?.join(' > ')}}: `, e);
        });
      }

      if (onError) {
        return onError(lazyQuery?.error);
      }

      if (!isProduction()) {
        return lazyQuery?.error.graphQLErrors.map(e => {
          if ((e.extensions?.exception as any)?.code === 'ENOTFOUND') {
            return (
              <ErrorFallback
                error={new Error(`Service "${e.message.split(' ')[2]}" is not reachable`)}
                componentStack={null}
              />
            );
          }
          return (
            <ErrorFallback
              error={new Error(e.message)}
              componentStack={JSON.stringify(e.extensions?.exception, undefined, 2)}
            />
          );
        });
      }

      return null;
    }
  }
  if (Array.isArray(query)) {
    const queries = query as QueryResult[];
    if (queries.some(q => q.loading)) {
      return hideLoader ? null : <Loader backdrop={false} />;
    }

    if (queries.some(q => !!q.error)) {
      queries
        .filter(q => !!q.error)
        .forEach(({ error }) => {
          if (!isProduction()) {
            error!.graphQLErrors.forEach(e => {
              console.error(`[ERROR]{${e.path?.join(' > ')}}: `, e);
            });
          }
        });

      if (ignoreErrors) {
        return children(queries.map(q => q.data));
      }

      if (onError) {
        return onError(queries[0].error!);
      }

      if (!isProduction()) {
        return queries
          .filter(q => !!q.error)
          .map(({ error }) =>
            error!.graphQLErrors.map(e => {
              if ((e.extensions?.exception as any)?.code === 'ENOTFOUND') {
                return (
                  <ErrorFallback
                    error={new Error(`Service "${e.message.split(' ')[2]}" is not reachable`)}
                    componentStack={null}
                  />
                );
              }
              return (
                <ErrorFallback
                  error={new Error(e.message)}
                  componentStack={JSON.stringify(e.extensions?.exception, undefined, 2)}
                />
              );
            })
          );
      }

      return null;
    }

    return children(queries.map(q => q.data));
  } else {
    const { loading, error, data } = query;

    if (loading) {
      return hideLoader ? null : <Loader backdrop={false} />;
    }

    if (error) {
      if (!isProduction()) {
        error.graphQLErrors.forEach(e => {
          console.error(`[ERROR]{${e.path?.join(' > ')}}: `, e);
        });
      }

      if (ignoreErrors) {
        return children(null);
      }

      if (onError) {
        return onError(error);
      }

      if (!isProduction()) {
        return error.graphQLErrors.map(e => {
          if ((e.extensions?.exception as any)?.code === 'ENOTFOUND') {
            return (
              <ErrorFallback
                error={new Error(`Service "${e.message.split(' ')[2]}" is not reachable`)}
                componentStack={null}
              />
            );
          }
          return (
            <ErrorFallback
              error={new Error(e.message)}
              componentStack={JSON.stringify(e.extensions?.exception, undefined, 2)}
            />
          );
        });
      }

      return null;
    }

    return children(data);
  }
}
